import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "../i18n";
import { RevealText } from "./Reveal";

const IMG_BASE = "https://customer-assets-rejwkqb3.emergentagent.net/job_reformas-acero/artifacts";

const PROJECTS = [
  {
    src: `${IMG_BASE}/2ok9r6wf_WhatsApp%20Image%202026-08-05%20at%2018.16.58%20%284%29.webp`,
    es: { t: "Fachada reformada", r: "Rehabilitación exterior · Córdoba" },
    en: { t: "Renovated façade", r: "Exterior rehabilitation · Córdoba" },
    span: "md:col-span-2 md:row-span-2",
    focal: "center",
  },
  {
    src: `${IMG_BASE}/gf23w02l_WhatsApp%20Image%202026-08-05%20at%2018.16.58%20%285%29.webp`,
    es: { t: "Baño reformado", r: "Alicatado, plato ducha y espejo LED" },
    en: { t: "Renovated bathroom", r: "Tiling, shower tray & LED mirror" },
    span: "",
    focal: "center",
  },
  {
    src: `${IMG_BASE}/g1pxfbqc_ba%C3%B1o.jpeg`,
    es: { t: "Baño moderno", r: "Ducha negra y espejo LED orgánico" },
    en: { t: "Modern bathroom", r: "Black shower & organic LED mirror" },
    span: "",
    focal: "center",
  },
  {
    src: `${IMG_BASE}/foyc4ymm_WhatsApp%20Image%202026-08-05%20at%2013.36.32.jpeg`,
    es: { t: "Piso reformado", r: "Pasillo, pintura y solado" },
    en: { t: "Renovated apartment", r: "Hallway, paint & flooring" },
    span: "",
    focal: "center",
  },
  {
    src: `${IMG_BASE}/8cwkyr75_WhatsApp%20Image%202026-08-05%20at%2013.36.35.webp`,
    es: { t: "Salón con papel decorativo", r: "Reforma integral · Córdoba" },
    en: { t: "Living room with feature wallpaper", r: "Full renovation · Córdoba" },
    span: "md:col-span-2",
    focal: "center",
  },
  {
    src: `${IMG_BASE}/9ky6h2g1_WhatsApp%20Image%202026-08-05%20at%2013.36.32.webp`,
    es: { t: "Salón-comedor", r: "Iluminación empotrada y acabados" },
    en: { t: "Living-dining room", r: "Recessed lighting & finishes" },
    span: "",
    focal: "center",
  },
  {
    src: `${IMG_BASE}/cjfa8lxe_WhatsApp%20Image%202026-08-05%20at%2013.36.32%20%281%29.webp`,
    es: { t: "Dormitorio principal", r: "Suelo laminado y pintura" },
    en: { t: "Main bedroom", r: "Laminate flooring & paint" },
    span: "",
    focal: "center",
  },
];

export default function Galeria() {
  const { t, lang } = useLang();
  const g = t.galeria;
  const [open, setOpen] = useState(null);

  return (
    <section id="galeria" className="relative py-24 md:py-32 grain">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex items-center gap-3 mb-8">
          <span className="h-px w-10 bg-brand-orange" />
          <span className="text-xs md:text-sm font-bold tracking-[0.25em] uppercase text-brand-orange">{g.overline}</span>
        </div>
        <RevealText
          as="h2"
          text={g.title}
          className="font-display font-semibold tracking-tighter leading-[0.95] text-4xl md:text-5xl lg:text-6xl block max-w-3xl"
        />
        <p className="mt-5 max-w-xl text-steel-300 md:text-lg">{g.sub}</p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 auto-rows-[280px] md:auto-rows-[320px] gap-3 md:gap-4">
          {PROJECTS.map((p, i) => {
            const meta = p[lang];
            return (
              <motion.button
                key={i}
                type="button"
                onClick={() => setOpen(i)}
                data-testid={`project-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-6%" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative overflow-hidden text-left ${p.span}`}
              >
                <img
                  src={p.src}
                  alt={meta.t}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 flex items-end justify-between gap-4">
                  <div>
                    <div className="text-[10px] md:text-xs font-bold tracking-[0.25em] uppercase text-brand-orange">{meta.r}</div>
                    <div className="mt-1 font-display font-semibold text-lg md:text-2xl leading-tight text-white">{meta.t}</div>
                  </div>
                  <span className="hidden md:inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/30 text-white text-xs opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300">
                    +
                  </span>
                </div>
                <span className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/10" />
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {open !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-10"
            onClick={() => setOpen(null)}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setOpen(null)}
              className="absolute top-5 right-5 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center transition-colors"
              data-testid="lightbox-close"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-6xl w-full"
            >
              <img
                src={PROJECTS[open].src}
                alt={PROJECTS[open][lang].t}
                className="w-full max-h-[82vh] object-contain"
              />
              <div className="mt-4">
                <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-orange">{PROJECTS[open][lang].r}</div>
                <div className="mt-1 font-display font-semibold text-2xl">{PROJECTS[open][lang].t}</div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
